import React, { useState } from 'react';
import {
  Box, Container, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, TextField,
  InputAdornment, Stack, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Visibility as ViewIcon, Edit as EditIcon } from '@mui/icons-material';
import type { Ticket, TicketStatus, TicketPriority } from '../types';
import { mockTickets } from '../services/mockData';
import TicketDetails from './TicketDetails';
import TicketForm from './TicketForm';

const TicketList: React.FC = () => {
  const [tickets] = useState<Ticket[]>(mockTickets);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  // Modal state
  const [detailTicketId, setDetailTicketId] = useState<string | null>(null);
  const [formTicketId, setFormTicketId] = useState<string | null | undefined>(undefined); // undefined = closed, null = new, string = edit

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch =
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status: TicketStatus) => {
    const colors: Record<string, string> = { new: 'info', open: 'primary', in_progress: 'warning', resolved: 'success', closed: 'default', reopened: 'error' };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: TicketPriority) => {
    const colors: Record<string, string> = { low: 'success', medium: 'info', high: 'warning', critical: 'error' };
    return colors[priority] || 'default';
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Support Tickets
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormTicketId(null)}>
            New Ticket
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth placeholder="Search tickets..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="reopened">Reopened</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
                <MenuItem value="all">All Priority</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* Tickets Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Assignee</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id} hover
                    sx={{ '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' } }}
                    onClick={() => setDetailTicketId(ticket.id)}
                  >
                    <TableCell><Typography variant="body2" fontWeight="medium">{ticket.id}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ maxWidth: 300 }}>{ticket.title}</Typography></TableCell>
                    <TableCell><Chip label={ticket.status.replace('_', ' ').toUpperCase()} color={getStatusColor(ticket.status) as any} size="small" /></TableCell>
                    <TableCell><Chip label={ticket.priority.toUpperCase()} color={getPriorityColor(ticket.priority) as any} size="small" variant="outlined" /></TableCell>
                    <TableCell><Typography variant="body2">{ticket.category}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{ticket.assignee || 'Unassigned'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{formatDate(ticket.created_at)}</Typography></TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailTicketId(ticket.id); }}><ViewIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFormTicketId(ticket.id); }}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredTickets.length} of {tickets.length} tickets
          </Typography>
        </Box>
      </Box>

      {/* Detail Modal */}
      <TicketDetails
        isOpen={!!detailTicketId}
        ticketId={detailTicketId}
        onClose={() => setDetailTicketId(null)}
        onEdit={(id) => { setDetailTicketId(null); setFormTicketId(id); }}
      />

      {/* Form Modal */}
      <TicketForm
        isOpen={formTicketId !== undefined}
        ticketId={formTicketId}
        onClose={() => setFormTicketId(undefined)}
        onSuccess={() => { /* TODO: refresh list */ }}
      />
    </Container>
  );
};

export default TicketList;
